import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Observable, Subscription } from 'rxjs';

export type ExportPhase = 'fetching' | 'building';

export interface ExportProgress {
  /** Rows fetched so far. */
  loaded: number;
  /** Expected total (from the pre-export count); 0 => indeterminate bar. */
  total: number;
  phase: ExportPhase;
}

/**
 * Progress dialog shown while an export runs (batched keyset fetch + file build).
 * Follows the system modal skeleton (docs/claims-v2/MODALS.md — `.app-modal` +
 * `panelClass: 'app-modal-pane'`). Determinate bar with % when the total is known;
 * indeterminate while building the file. Cancel sets a flag the ExportService checks
 * between batches.
 */
@Component({
  selector: 'app-export-progress-modal',
  templateUrl: './export-progress-modal.component.html',
  styleUrls: ['./export-progress-modal.component.scss'],
})
export class ExportProgressModalComponent implements OnInit, OnDestroy {
  progress: ExportProgress = { loaded: 0, total: 0, phase: 'fetching' };
  private sub?: Subscription;

  constructor(
    private dialogRef: MatDialogRef<ExportProgressModalComponent>,
    @Inject(MAT_DIALOG_DATA)
    public data: {
      progress$: Observable<ExportProgress>;
      onCancel: () => void;
    },
  ) {
    // Must not be dismissed by ESC/backdrop — the service owns its lifecycle.
    this.dialogRef.disableClose = true;
  }

  ngOnInit(): void {
    this.sub = this.data.progress$.subscribe((p) => (this.progress = p));
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }

  get percent(): number {
    const p = this.progress;
    if (!p.total) return 0;
    return Math.min(100, Math.floor((p.loaded / p.total) * 100));
  }

  get indeterminate(): boolean {
    return this.progress.phase === 'building' || !this.progress.total;
  }

  cancel(): void {
    this.data.onCancel();
  }
}
